import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiBriefcase, FiHash, FiUser, FiMapPin } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Drawer from '../../components/Drawer/Drawer';
import drawerStyles from '../../components/Drawer/Drawer.module.css';
import styles from '../../styles/adminPanels.module.css';
import axiosClient from '../../api/axiosClient';

interface KybProfile {
  id: string;
  account_name: string;
  account_email: string;
  company_name: string;
  registration_number: string;
  gst_pan: string;
  company_address: string;
  incorporation_country: string;
  ubo_name: string;
  ubo_designation: string;
  ubo_ownership_pct: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface KybDoc {
  id: string;
  account_name: string;
  account_email: string;
  document_type: string;
  file_path: string;
  status: 'pending' | 'approved' | 'rejected';
}

const STATUS_COLORS: Record<string, string> = { pending: '#e8a86a', approved: '#5cb87d', rejected: '#e2534a' };
const DOC_TYPE_LABEL: Record<string, string> = {
  registration_certificate: 'Company Registration Certificate',
  gst_pan_proof: 'GST / PAN Proof',
  ubo_id_proof: "UBO's Government ID Proof",
};
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

const AdminKyb: React.FC = () => {
  const [profiles, setProfiles] = useState<KybProfile[]>([]);
  const [documents, setDocuments] = useState<KybDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<KybProfile | null>(null);

  const load = () => {
    setLoading(true);
    axiosClient.get('/admin/kyb').then(({ data }) => {
      setProfiles(data.profiles);
      setDocuments(data.documents);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateProfileStatus = async (id: string, status: string) => {
    try {
      await axiosClient.patch(`/admin/kyb/profile/${id}/status`, { status });
      toast.success(`Company profile ${status}.`);
      setSelected(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update status.');
    }
  };
  const updateDocStatus = async (id: string, status: string) => {
    try {
      await axiosClient.patch(`/admin/kyb/documents/${id}/status`, { status });
      toast.success(`Document ${status}.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update status.');
    }
  };

  const docsFor = (email: string) => documents.filter((d) => d.account_email === email);

  return (
    <AdminShell>
      <PageHeader title="KYB review" subtitle="Institutional partners — company registration, GST/PAN, and UBO verification. Click a row for full details." action={<AdminWalletBadge />} />
      <div className={styles.page}>
        <div className={styles.tableWrap}>
          {loading && <div className={styles.loading}><span className={styles.spinnerLg} /></div>}
          {!loading && profiles.length === 0 && <div className={styles.empty}>No KYB submissions yet.</div>}
          {!loading && profiles.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr><th>User</th><th>Company</th><th>Reg. no / GST-PAN</th><th>UBO</th><th>Status</th></tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id} onClick={() => setSelected(p)} style={{ cursor: 'pointer' }}>
                    <td><div className={styles.nameCell}>{p.account_name}</div><div className={styles.subCell}>{p.account_email}</div></td>
                    <td className={styles.nameCell}>{p.company_name}</td>
                    <td><div className={styles.subCell}>{p.registration_number}</div><div className={styles.subCell}>{p.gst_pan}</div></td>
                    <td><div className={styles.subCell}>{p.ubo_name}</div><div className={styles.subCell}>{p.ubo_designation}</div></td>
                    <td><span className={styles.statusPill} style={{ color: STATUS_COLORS[p.status], borderColor: STATUS_COLORS[p.status] }}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="KYB submission">
        {selected && (
          <>
            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}><FiUser style={{ marginRight: 6 }} />Account</div>
              <div className={drawerStyles.detailValue}>{selected.account_name}</div>
              <div className={styles.subCell}>{selected.account_email}</div>
            </div>

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}><FiBriefcase style={{ marginRight: 4 }} />Company name</div>
              <div className={drawerStyles.detailValue}>{selected.company_name || '—'}</div>
            </div>

            <div className={drawerStyles.detailGrid}>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}><FiHash style={{ marginRight: 4 }} />Registration no.</div>
                <div className={drawerStyles.detailValue}>{selected.registration_number || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>GST / PAN</div>
                <div className={drawerStyles.detailValue}>{selected.gst_pan || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Incorporation country</div>
                <div className={drawerStyles.detailValue}>{selected.incorporation_country || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Submitted</div>
                <div className={drawerStyles.detailValue}>{new Date(selected.created_at).toLocaleString()}</div>
              </div>
            </div>

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}><FiMapPin style={{ marginRight: 4 }} />Company address</div>
              <div className={drawerStyles.detailValue}>{selected.company_address || '—'}</div>
            </div>

            <div className={drawerStyles.detailGrid}>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>UBO name</div>
                <div className={drawerStyles.detailValue}>{selected.ubo_name || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>UBO designation</div>
                <div className={drawerStyles.detailValue}>{selected.ubo_designation || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>UBO ownership</div>
                <div className={drawerStyles.detailValue}>{selected.ubo_ownership_pct ? `${selected.ubo_ownership_pct}%` : '—'}</div>
              </div>
            </div>

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>Uploaded documents</div>
              {docsFor(selected.account_email).length === 0 && <div className={styles.subCell}>No documents uploaded yet.</div>}
              {docsFor(selected.account_email).map((d) => (
                <div key={d.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--line)' }}>
                  <a className={drawerStyles.fileLink} style={{ marginBottom: 8, display: 'block' }} href={`${API_ORIGIN}${d.file_path}`} target="_blank" rel="noreferrer">{DOC_TYPE_LABEL[d.document_type] || d.document_type}</a>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className={styles.statusPill} style={{ color: STATUS_COLORS[d.status], borderColor: STATUS_COLORS[d.status] }}>{d.status}</span>
                    <div className={styles.actionsCell}>
                      {d.status !== 'approved' && <button className={styles.actionBtn} onClick={() => updateDocStatus(d.id, 'approved')}>Approve</button>}
                      {d.status !== 'rejected' && <button className={styles.actionBtnReject} onClick={() => updateDocStatus(d.id, 'rejected')}>Reject</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>Current status</div>
              <span className={styles.statusPill} style={{ color: STATUS_COLORS[selected.status], borderColor: STATUS_COLORS[selected.status] }}>{selected.status}</span>
            </div>
          </>
        )}
        {selected && (
          <div className={drawerStyles.footer} style={{ position: 'sticky', bottom: 0, background: 'var(--panel)', margin: '24px -24px -24px' }}>
            {selected.status !== 'approved' && (
              <button className={styles.actionBtn} onClick={() => updateProfileStatus(selected.id, 'approved')}>Approve profile</button>
            )}
            {selected.status !== 'rejected' && (
              <button className={styles.actionBtnReject} onClick={() => updateProfileStatus(selected.id, 'rejected')}>Reject profile</button>
            )}
          </div>
        )}
      </Drawer>
    </AdminShell>
  );
};

export default AdminKyb;
